import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useStore, productSubcat } from "../context/StoreContext";
import { Icons } from "../components/Icons";
import { detectColorsForSet } from "../utils/colorDetect";
import { contrastText } from "../utils/colorNames";
import { imgUrl } from "../utils/imageUrl";
import "./Admin.css";

/* ================= FIREBASE ================= */
import { db } from "../firebase";
import { collection, addDoc, serverTimestamp, doc, updateDoc, deleteDoc } from "firebase/firestore";

/* ================= CLOUDINARY CONFIG ================= */
const CLOUDINARY_UPLOAD_PRESET = "cityshoppingcenter";
const CLOUDINARY_CLOUD_NAME = "dihaynvq1";

const EMPTY_FORM = {
  name: "", cat: "", subCat: "", price: "", oldPrice: "",
  desc: "", badge: "", dimensions: "", stock: "1", colors: [], colorName: "",
};

export default function Admin() {
  const {
    products, addProduct, deleteProduct, updateProduct,
    waNum, setWaNum, adminPass, setAdminPass, Rs,
    categories, subcatsFor, catNames,
    addCategory, updateCategory, deleteCategory,
    addSubcategory, updateSubcategory, deleteSubcategory, addSubcategoryToCat,
    importLegacyNecklaceCats, necklaceCats,
    catCount, subcatCount,
  } = useStore();

  /* ================= AUTH ================= */
  const [tab, setTab] = useState("add");
  const [isAuth, setIsAuth] = useState(false);
  const [inputPass, setInputPass] = useState("");
  const [loginError, setLoginError] = useState("");

  /* ================= PRODUCT FORM ================= */
  const [form, setForm] = useState(EMPTY_FORM);
  const [imgFiles, setImgFiles] = useState([]);          // { file, preview }

  /* Colour variants — one product can be sold in several colours, each with
     its own photos. A variant carries: a name (auto-filled from the photo,
     then editable), a swatch hex, any already-uploaded photos (edit mode),
     and freshly-picked files waiting to upload.
       { key, name, hex, existing:[url], files:[{file,preview}], detecting, edited } */
  const [variants, setVariants] = useState([]);
  const variantKey = React.useRef(1);

  const [uploading, setUploading] = useState(false);
  const [addOk, setAddOk] = useState(false);
  const [formError, setFormError] = useState("");

  /* ================= EDIT MODE ================= */
  const [editId, setEditId] = useState(null);
  const [editImgFiles, setEditImgFiles] = useState([]);
  const [editExistingImgs, setEditExistingImgs] = useState([]);
  const [editOk, setEditOk] = useState(false);
  const [editUploading, setEditUploading] = useState(false);

  /* ================= AUTOMATIC COLOUR DETECTION ================= */
  const [detected, setDetected] = useState([]);       // [{name,hex,pct}]
  const [detecting, setDetecting] = useState(false);
  const [colorSource, setColorSource] = useState("auto"); // 'auto' | 'manual'

  /* ================= SETTINGS ================= */
  const [swa, setSwa] = useState(waNum);
  const [spass, setSpass] = useState("");
  const [settingsOk, setSettingsOk] = useState(false);

  /* ================= CATEGORY MANAGER ================= */
  const [newCatName, setNewCatName] = useState("");
  const [catBusy, setCatBusy] = useState(false);
  const [catMsg, setCatMsg] = useState(null);          // { ok, text }
  const [editingCatId, setEditingCatId] = useState(null);
  const [editingCatName, setEditingCatName] = useState("");
  const [openCatId, setOpenCatId] = useState(null);   // which category is expanded
  const [newSubName, setNewSubName] = useState("");
  /* Quick "pick a category → add subcategory" panel */
  const [quickSubCat, setQuickSubCat] = useState("");
  const [quickSubName, setQuickSubName] = useState("");
  const [editingSubId, setEditingSubId] = useState(null);
  const [editingSubName, setEditingSubName] = useState("");

  /* ================= PRODUCT LIST FILTER ================= */
  const [listFilter, setListFilter] = useState("");

  const isEditing = Boolean(editId);

  /* Category options come straight from the database. When none exist
     yet, fall back to the values products already carry so the admin can
     still edit existing products before setting the hierarchy up. */
  const catOptions = catNames;
  const subOptions = useMemo(() => subcatsFor(form.cat), [subcatsFor, form.cat]);

  /* ========================================================
     AUTOMATIC COLOUR DETECTION
     Runs whenever the set of images in the form changes. The
     admin never has to type a colour — they only see the result.
     ======================================================== */
  const runDetection = useCallback(async () => {
    const queued = isEditing ? editImgFiles : imgFiles;
    const alreadyUploaded = isEditing ? editExistingImgs : [];

    // Local files first: they read instantly and need no CORS.
    // If the product only has already-uploaded photos, read those
    // back through Cloudinary at a small size instead.
    const localFiles = queued.map((i) => i.file);
    const sources = localFiles.length
      ? localFiles
      : alreadyUploaded.slice(0, 3).map((u) => imgUrl(u, { w: 200 }));

    if (!sources.length) { setDetected([]); return; }

    setDetecting(true);
    try {
      const res = await detectColorsForSet(sources, { max: 3 });
      setDetected(res);
    } catch (err) {
      console.error("Colour detection failed:", err);
      setDetected([]);
    }
    setDetecting(false);
  }, [isEditing, editImgFiles, imgFiles, editExistingImgs]);

  useEffect(() => {
    if (colorSource !== "auto") return;
    // Small debounce so picking five files at once triggers one pass
    let cancelled = false;
    const t = setTimeout(() => { if (!cancelled) runDetection(); }, 120);
    return () => { cancelled = true; clearTimeout(t); };
  }, [runDetection, colorSource]);

  /* ================= LOGIN ================= */
  const handleLogin = () => {
    if (inputPass.trim() === adminPass) { setIsAuth(true); setLoginError(""); }
    else { setLoginError("That password is not correct."); setInputPass(""); }
  };

  /* ================= FORM HELPERS ================= */
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  /* ================= IMAGE HANDLERS ================= */
  const pickValidImages = (fileList) => {
    const files = Array.from(fileList || []);
    const valid = files.filter((f) => {
      if (!f.type.startsWith("image/")) { alert(`${f.name}: only images allowed`); return false; }
      if (f.size > 5 * 1024 * 1024) { alert(`${f.name}: must be under 5MB`); return false; }
      return true;
    });
    return valid.map((f) => ({ file: f, preview: URL.createObjectURL(f) }));
  };

  const handleImgs = (e) => {
    const items = pickValidImages(e.target.files);
    if (isEditing) setEditImgFiles((prev) => [...prev, ...items]);
    else setImgFiles((prev) => [...prev, ...items]);
    e.target.value = "";
  };

  const removeNewImg = (idx) => setImgFiles((prev) => prev.filter((_, i) => i !== idx));
  const removeEditNewImg = (idx) => setEditImgFiles((prev) => prev.filter((_, i) => i !== idx));
  const removeExistingImg = (idx) => setEditExistingImgs((prev) => prev.filter((_, i) => i !== idx));

  /* ================= COLOUR VARIANTS ================= */
  const makeVariant = () => ({
    key: variantKey.current++,
    name: "", hex: "#CCCCCC",
    existing: [], files: [],
    detecting: false, edited: false,
  });

  const addVariant = () => setVariants((v) => [...v, makeVariant()]);

  const removeVariant = (key) =>
    setVariants((v) => v.filter((x) => x.key !== key));

  const patchVariant = (key, patch) =>
    setVariants((v) => v.map((x) => (x.key === key ? { ...x, ...patch } : x)));

  const setVariantName = (key, name) =>
    setVariants((v) => v.map((x) => (x.key === key ? { ...x, name, edited: true } : x)));

  /* Read the dominant colour from a variant's photos and fill in its swatch
     and (unless the admin already typed one) its name. */
  const detectVariant = useCallback(async (key, sources) => {
    if (!sources.length) return;
    patchVariant(key, { detecting: true });
    try {
      const res = await detectColorsForSet(sources, { max: 1 });
      const top = res && res[0];
      if (top) {
        setVariants((v) => v.map((x) => {
          if (x.key !== key) return x;
          return {
            ...x,
            hex: top.hex,
            name: x.edited && x.name.trim() ? x.name : top.name,
            detecting: false,
          };
        }));
      } else {
        patchVariant(key, { detecting: false });
      }
    } catch (err) {
      console.error("Variant colour detection failed:", err);
      patchVariant(key, { detecting: false });
    }
  }, []);

  const addVariantPhotos = (key, e) => {
    const items = pickValidImages(e.target.files);
    e.target.value = "";
    if (!items.length) return;
    setVariants((v) => v.map((x) =>
      x.key === key ? { ...x, files: [...x.files, ...items] } : x));
    // Detect from the newly-picked local files (instant, no CORS).
    detectVariant(key, items.map((i) => i.file));
  };

  const removeVariantNewPhoto = (key, idx) =>
    setVariants((v) => v.map((x) =>
      x.key === key ? { ...x, files: x.files.filter((_, i) => i !== idx) } : x));

  const removeVariantExisting = (key, idx) =>
    setVariants((v) => v.map((x) =>
      x.key === key ? { ...x, existing: x.existing.filter((_, i) => i !== idx) } : x));

  /* ================= CLOUDINARY UPLOAD ================= */
  const uploadOne = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
    try {
      const res = await axios.post(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/auto/upload`,
        formData, { headers: { "Content-Type": "multipart/form-data" } }
      );
      return res.data.secure_url;
    } catch (err) {
      console.error("Cloudinary Error:", err.response?.data || err);
      alert(err.response?.data?.error?.message || "Image upload failed!");
      return null;
    }
  };

  /* Upload every variant's new photos and return the data saved on the
     product: `colorVariants` (name + hex + image urls, in order) and the
     `colorImages` map kept for backward-compatibility with older code. */
  const buildVariants = async () => {
    const colorVariants = [];
    const colorImages = {};

    for (const v of variants) {
      const name = (v.name || "").trim();
      // A variant needs a name and at least one photo to be saved.
      const kept = v.existing || [];
      const uploaded = [];
      for (const item of (v.files || [])) {
        const url = await uploadOne(item.file);
        if (!url) return null;              // abort the whole save on failure
        uploaded.push(url);
      }
      const images = [...kept, ...uploaded];
      if (!name || !images.length) continue;
      if (colorImages[name]) continue;      // ignore a duplicate colour name

      colorVariants.push({ name, hex: v.hex || "#CCCCCC", images });
      colorImages[name] = images;
    }

    return { colorVariants, colorImages };
  };

  /* ================= VALIDATION ================= */
  const validate = () => {
    if (!form.name.trim()) return "Product name is required.";
    const price = parseFloat(form.price);
    if (!price || isNaN(price)) return "Enter a valid price.";
    if (catOptions.length > 0 && !form.cat) return "Choose a category.";
    if (subOptions.length > 0 && !form.subCat) return "Choose a subcategory.";
    return "";
  };

  /* The colour record saved with a product.

     When the product has colour variants, the first variant is its primary
     colour. Otherwise we fall back to the colour read automatically from
     the main product photos, so single-colour products still work. */
  const colorPayload = (colorVariants) => {
    const list = detected.length ? detected : [];

    if (colorVariants && colorVariants.length) {
      const first = colorVariants[0];
      return {
        detectedColors: list,
        colorName: first.name,
        color: { name: first.name, hex: first.hex },
        colorSource: "variants",
      };
    }

    return {
      detectedColors: list,
      colorName: "",
      color: list[0] ? { name: list[0].name, hex: list[0].hex } : null,
      colorSource: list.length ? "auto" : "none",
    };
  };

  /* ================= ADD PRODUCT ================= */
  const handleAdd = async () => {
    const err = validate();
    if (err) { setFormError(err); return; }
    setFormError("");

    setUploading(true);
    const urls = [];
    for (const item of imgFiles) {
      const url = await uploadOne(item.file);
      if (!url) { setUploading(false); return; }
      urls.push(url);
    }

    const built = await buildVariants();
    if (built === null) { setUploading(false); return; }
    const { colorVariants, colorImages } = built;
    const firstColorUrl = colorVariants[0]?.images[0] || "";

    const stock = form.stock === "" ? 1 : Math.max(0, parseInt(form.stock, 10) || 0);

    const payload = {
      name: form.name.trim(),
      cat: form.cat,
      subCat: form.subCat || "",
      // mirrored so anything still reading the old field keeps working
      necklaceCat: form.cat === "Necklaces" ? (form.subCat || "") : "",
      price: parseFloat(form.price),
      oldPrice: form.oldPrice ? parseFloat(form.oldPrice) : null,
      desc: form.desc.trim(),
      badge: form.badge || "",
      dimensions: form.dimensions.trim(),
      colors: colorVariants.map((v) => v.name),
      colorVariants,
      colorImages,
      ...colorPayload(colorVariants),
      img: urls[0] || firstColorUrl || "",
      images: urls,
      stock,
      isActive: true,
    };

    try {
      await addDoc(collection(db, "products"), {
        ...payload,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      addProduct(payload);

      setAddOk(true);
      setTimeout(() => setAddOk(false), 2500);
      resetForm();
    } catch (error) {
      console.error("Firebase Error:", error);
      setFormError("Could not save the product. Check your connection and try again.");
    }
    setUploading(false);
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setImgFiles([]);
    setVariants([]);
    setDetected([]);
    setColorSource("auto");
  };

  /* ================= EDIT PRODUCT ================= */
  const startEdit = (p) => {
    setEditId(p.id);

    /* Rebuild the variant rows from whatever the product has saved:
       prefer the new colorVariants list; fall back to the older
       colorImages map (+ any saved swatch hexes) for older products. */
    const savedVariants = Array.isArray(p.colorVariants) && p.colorVariants.length
      ? p.colorVariants.map((v) => ({
          key: variantKey.current++,
          name: v.name || "",
          hex: v.hex || "#CCCCCC",
          existing: Array.isArray(v.images) ? v.images : [],
          files: [],
          detecting: false,
          edited: true,
        }))
      : Object.entries(p.colorImages || {}).map(([name, imgs]) => ({
          key: variantKey.current++,
          name,
          hex: (p.color && p.color.name === name && p.color.hex) || "#CCCCCC",
          existing: Array.isArray(imgs) ? imgs : [],
          files: [],
          detecting: false,
          edited: true,
        }));
    setVariants(savedVariants);

    setForm({
      name: p.name,
      cat: p.cat || "",
      subCat: productSubcat(p),
      price: p.price,
      oldPrice: p.oldPrice || "",
      desc: p.desc || "",
      badge: p.badge || "",
      dimensions: p.dimensions || "",
      stock: typeof p.stock === "number" ? String(p.stock) : "1",
      colors: savedVariants.map((v) => v.name),
      colorName: p.colorName || (p.color && p.color.name) || "",
    });

    setEditExistingImgs(p.images && p.images.length ? p.images : p.img ? [p.img] : []);
    setEditImgFiles([]);
    setDetected(Array.isArray(p.detectedColors) ? p.detectedColors : []);
    setColorSource(p.colorSource === "manual" ? "manual" : "auto");
    setFormError("");
    setTab("edit");
  };

  const cancelEdit = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setEditExistingImgs([]);
    setEditImgFiles([]);
    setVariants([]);
    setDetected([]);
    setColorSource("auto");
    setFormError("");
    setTab("list");
  };

  const handleUpdate = async () => {
    const err = validate();
    if (err) { setFormError(err); return; }
    setFormError("");

    setEditUploading(true);
    const newUrls = [];
    for (const item of editImgFiles) {
      const url = await uploadOne(item.file);
      if (!url) { setEditUploading(false); return; }
      newUrls.push(url);
    }

    const built = await buildVariants();
    if (built === null) { setEditUploading(false); return; }
    const { colorVariants, colorImages } = built;
    const firstColorUrl = colorVariants[0]?.images[0] || "";

    const allImages = [...editExistingImgs, ...newUrls];
    const stock = form.stock === "" ? 0 : Math.max(0, parseInt(form.stock, 10) || 0);

    const updatedData = {
      name: form.name.trim(),
      cat: form.cat,
      subCat: form.subCat || "",
      necklaceCat: form.cat === "Necklaces" ? (form.subCat || "") : "",
      price: parseFloat(form.price),
      oldPrice: form.oldPrice ? parseFloat(form.oldPrice) : null,
      desc: form.desc.trim(),
      badge: form.badge || "",
      dimensions: form.dimensions.trim(),
      colors: colorVariants.map((v) => v.name),
      colorVariants,
      colorImages,
      ...colorPayload(colorVariants),
      img: allImages[0] || firstColorUrl || "",
      images: allImages,
      stock,
      updatedAt: serverTimestamp(),
    };

    try {
      await updateDoc(doc(db, "products", editId), updatedData);
      updateProduct(editId, updatedData);
      setEditOk(true);
      setTimeout(() => setEditOk(false), 2500);
      cancelEdit();
    } catch (err2) {
      console.error("Update error:", err2);
      setFormError("Could not save your changes. Try again.");
    }
    setEditUploading(false);
  };

  /* ================= DELETE ================= */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product? This cannot be undone.")) return;
    try {
      await deleteDoc(doc(db, "products", id));
      deleteProduct(id);
    } catch (err) {
      console.error("Delete error:", err);
      alert("Could not delete the product.");
    }
  };

  /* ================= SETTINGS ================= */
  const handleSaveSettings = () => {
    if (swa.trim()) setWaNum(swa.trim());
    if (spass.trim()) setAdminPass(spass.trim());
    setSpass("");
    setSettingsOk(true);
    setTimeout(() => setSettingsOk(false), 2500);
  };

  /* ================= CATEGORY MANAGER HANDLERS ================= */
  const flash = (ok, text) => {
    setCatMsg({ ok, text });
    setTimeout(() => setCatMsg(null), 3500);
  };

  const handleAddCategory = async () => {
    if (!newCatName.trim()) return flash(false, "Type a category name first.");
    setCatBusy(true);
    const res = await addCategory(newCatName);
    setCatBusy(false);
    if (!res.ok) return flash(false, res.msg);
    setNewCatName("");
    flash(true, "Category created.");
  };

  const handleRenameCategory = async (id) => {
    setCatBusy(true);
    const res = await updateCategory(id, editingCatName);
    setCatBusy(false);
    if (!res.ok) return flash(false, res.msg);
    setEditingCatId(null);
    flash(true, res.moved ? `Renamed. ${res.moved} product${res.moved === 1 ? "" : "s"} updated.` : "Category renamed.");
  };

  const handleDeleteCategory = async (id, name) => {
    if (!window.confirm(`Delete the category "${name}" and its subcategories?`)) return;
    setCatBusy(true);
    const res = await deleteCategory(id);
    setCatBusy(false);
    flash(res.ok, res.ok ? "Category deleted." : res.msg);
  };

  const handleAddSub = async (categoryId) => {
    if (!newSubName.trim()) return flash(false, "Type a subcategory name first.");
    setCatBusy(true);
    const res = await addSubcategory(categoryId, newSubName);
    setCatBusy(false);
    if (!res.ok) return flash(false, res.msg);
    setNewSubName("");
    flash(true, "Subcategory created.");
  };

  /* Add a subcategory to any existing category picked from the dropdown.
     Works even for categories that only exist on products so far. */
  const handleQuickAddSub = async () => {
    if (!quickSubCat) return flash(false, "Select a category first.");
    if (!quickSubName.trim()) return flash(false, "Type a subcategory name.");
    setCatBusy(true);
    const res = await addSubcategoryToCat(quickSubCat, quickSubName);
    setCatBusy(false);
    if (!res.ok) return flash(false, res.msg);
    setQuickSubName("");
    flash(true, `"${quickSubName.trim()}" added under ${res.categoryName}.`);
  };

  const handleRenameSub = async (id) => {
    setCatBusy(true);
    const res = await updateSubcategory(id, editingSubName);
    setCatBusy(false);
    if (!res.ok) return flash(false, res.msg);
    setEditingSubId(null);
    flash(true, res.moved ? `Renamed. ${res.moved} product${res.moved === 1 ? "" : "s"} updated.` : "Subcategory renamed.");
  };

  const handleDeleteSub = async (id, name) => {
    if (!window.confirm(`Delete the subcategory "${name}"?`)) return;
    setCatBusy(true);
    const res = await deleteSubcategory(id);
    setCatBusy(false);
    flash(res.ok, res.ok ? "Subcategory deleted." : res.msg);
  };

  const handleImportLegacy = async () => {
    setCatBusy(true);
    const res = await importLegacyNecklaceCats();
    setCatBusy(false);
    flash(res.ok, res.ok ? `Imported ${res.added} subcategor${res.added === 1 ? "y" : "ies"}.` : res.msg);
  };

  /* ================= SHARED FORM UI ================= */
  const renderFormFields = (isEdit = false) => {
    const existingImgs = isEdit ? editExistingImgs : [];
    const queuedImgs = isEdit ? editImgFiles : imgFiles;
    const totalImgs = existingImgs.length + queuedImgs.length;

    return (
      <>
        {(isEdit ? editOk : addOk) && (
          <div className="msg-success">
            {Icons.check()} {isEdit ? "Product updated." : "Product added."}
          </div>
        )}
        {formError && <div className="msg-error">{formError}</div>}

        {/* ── Basics ─────────────────────────────────────── */}
        <fieldset className="admin-fieldset">
          <legend>Product details</legend>

          <div className="admin-field">
            <label htmlFor="p-name">Product name *</label>
            <input id="p-name" className="form-input" value={form.name} onChange={set("name")}
              placeholder="e.g. Classic Zircon Ring" />
          </div>

          <div className="admin-grid-2">
            <div className="admin-field">
              <label htmlFor="p-cat">Category *</label>
              <select id="p-cat" className="form-select" value={form.cat}
                onChange={(e) => setForm((f) => ({ ...f, cat: e.target.value, subCat: "" }))}>
                <option value="">— Select a category —</option>
                {catOptions.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              {catOptions.length === 0 && (
                <p className="admin-hint">
                  No categories yet. Create them in the Categories tab first.
                </p>
              )}
            </div>

            <div className="admin-field">
              <label htmlFor="p-sub">Subcategory {subOptions.length > 0 && "*"}</label>
              <select id="p-sub" className="form-select" value={form.subCat}
                onChange={set("subCat")} disabled={!form.cat || subOptions.length === 0}>
                <option value="">
                  {!form.cat
                    ? "— Choose a category first —"
                    : subOptions.length === 0
                      ? "— No subcategories in this category —"
                      : "— Select a subcategory —"}
                </option>
                {subOptions.map((s) => <option key={s.id || s.name} value={s.name}>{s.name}</option>)}
              </select>
              {form.cat && subOptions.length === 0 && (
                <p className="admin-hint">
                  Add subcategories to “{form.cat}” in the Categories tab.
                </p>
              )}
            </div>
          </div>

          <div className="admin-grid-3">
            <div className="admin-field">
              <label htmlFor="p-price">Price (Rs.) *</label>
              <input id="p-price" type="number" className="form-input" value={form.price}
                onChange={set("price")} placeholder="0" min="0" />
            </div>
            <div className="admin-field">
              <label htmlFor="p-old">Old price (Rs.)</label>
              <input id="p-old" type="number" className="form-input" value={form.oldPrice}
                onChange={set("oldPrice")} placeholder="For discount display" min="0" />
            </div>
            <div className="admin-field">
              <label htmlFor="p-stock">Stock</label>
              <input id="p-stock" type="number" className="form-input" value={form.stock}
                onChange={set("stock")} placeholder="1" min="0" />
              <p className="admin-hint">Set to 0 to show the piece as sold out.</p>
            </div>
          </div>

          <div className="admin-grid-2">
            <div className="admin-field">
              <label htmlFor="p-dim">Size / dimensions</label>
              <input id="p-dim" className="form-input" value={form.dimensions}
                onChange={set("dimensions")} placeholder="e.g. Ring size 6–9, length 18cm" />
            </div>
            <div className="admin-field">
              <label htmlFor="p-badge">Badge</label>
              <select id="p-badge" className="form-select" value={form.badge} onChange={set("badge")}>
                <option value="">No badge</option>
                <option>New</option>
                <option>Sale</option>
                <option>Hot</option>
                <option>Limited</option>
              </select>
            </div>
          </div>

          <div className="admin-field">
            <label htmlFor="p-desc">Description</label>
            <textarea id="p-desc" className="form-textarea" value={form.desc}
              onChange={set("desc")} placeholder="What makes this piece special?" />
          </div>
        </fieldset>

        {/* ── Images ─────────────────────────────────────── */}
        <fieldset className="admin-fieldset">
          <legend>Product images</legend>
          <p className="admin-hint admin-hint--top">
            The default gallery, shown before a shopper picks a colour.
          </p>

          {totalImgs > 0 && (
            <div className="admin-img-grid">
              {existingImgs.map((url, i) => (
                <div key={`e${i}`} className="admin-img-thumb-wrap">
                  <img src={imgUrl(url, { w: 180 })} className="admin-img-thumb" alt="" loading="lazy" />
                  {i === 0 && <span className="admin-img-main-badge">Main</span>}
                  <button className="admin-img-remove-x" type="button"
                    onClick={() => removeExistingImg(i)} aria-label="Remove image">×</button>
                </div>
              ))}
              {queuedImgs.map((item, i) => (
                <div key={`n${i}`} className="admin-img-thumb-wrap">
                  <img src={item.preview} className="admin-img-thumb" alt="" />
                  {isEdit
                    ? <span className="admin-img-new-badge">New</span>
                    : (i === 0 && <span className="admin-img-main-badge">Main</span>)}
                  <button className="admin-img-remove-x" type="button"
                    onClick={() => (isEdit ? removeEditNewImg(i) : removeNewImg(i))}
                    aria-label="Remove image">×</button>
                </div>
              ))}
            </div>
          )}

          <label className="admin-img-upload-area">
            <span className="admin-img-upload-icon">{Icons.upload(30)}</span>
            <span className="admin-img-upload-text">Add images</span>
            <span className="admin-img-upload-hint">JPG, PNG or WEBP · up to 5MB each · pick several at once</span>
            <input type="file" accept="image/*" multiple onChange={handleImgs} hidden />
          </label>

          {/* ── Automatic colour detection ─────────────────── */}
          <div className="admin-autocolor">
            <div className="admin-autocolor__head">
              <span className="admin-autocolor__title">
                Detected colour{detected.length === 1 ? "" : "s"}
              </span>
              <span className="admin-autocolor__mode">
                {colorSource === "auto" ? "From the uploaded image" : "Detection paused"}
              </span>
            </div>

            {detecting ? (
              <div className="admin-autocolor__state">
                <span className="admin-spinner" aria-hidden="true" />
                Reading the image…
              </div>
            ) : detected.length > 0 ? (
              <>
                <ul className="admin-autocolor__list">
                  {detected.map((c, i) => (
                    <li key={c.name} className="admin-autocolor__chip">
                      <span className="admin-autocolor__swatch"
                        style={{ background: c.hex, color: contrastText(c.hex) }}>
                        {i === 0 ? "1" : ""}
                      </span>
                      <span className="admin-autocolor__text">
                        <strong>{c.name}</strong>
                        <code>{c.hex}</code>
                      </span>
                      <span className="admin-autocolor__pct">{c.pct}%</span>
                    </li>
                  ))}
                </ul>
                <p className="admin-hint">
                  The first colour is saved as the product colour and appears in every order message.
                </p>
              </>
            ) : (
              <div className="admin-autocolor__state admin-autocolor__state--empty">
                Add an image and the colour is read automatically.
              </div>
            )}

            <div className="admin-autocolor__actions">
              <button type="button" className="admin-mini-btn"
                onClick={runDetection} disabled={detecting || totalImgs === 0}>
                Re-detect
              </button>
              <button type="button" className="admin-mini-btn"
                onClick={() => setColorSource((s) => (s === "auto" ? "manual" : "auto"))}>
                {colorSource === "auto" ? "Pause auto-detection" : "Resume auto-detection"}
              </button>
            </div>
          </div>
        </fieldset>

        {/* ── Colour variants ─────────────────────────────── */}
        <fieldset className="admin-fieldset">
          <legend>Colours <span className="admin-legend-opt">optional</span></legend>
          <p className="admin-hint admin-hint--top">
            Add a colour for each way this product is sold. Upload that colour's
            photos and the colour name is read automatically — edit it if you
            like. On the product page shoppers tap a colour to see its photos.
          </p>

          {variants.map((v) => {
            const total = (v.existing?.length || 0) + (v.files?.length || 0);
            return (
              <div key={v.key} className="admin-variant">
                <div className="admin-variant__head">
                  <span className="admin-variant__swatch"
                    style={{ background: v.hex, color: contrastText(v.hex) }}>
                    {v.detecting ? "…" : ""}
                  </span>
                  <input
                    className="form-input admin-variant__name"
                    value={v.name}
                    onChange={(e) => setVariantName(v.key, e.target.value)}
                    placeholder={v.detecting ? "Reading colour…" : "Colour name (e.g. Gold, Green)"}
                  />
                  <button type="button" className="admin-variant__remove"
                    onClick={() => removeVariant(v.key)} aria-label="Remove colour">×</button>
                </div>

                {total > 0 && (
                  <div className="admin-img-grid">
                    {(v.existing || []).map((url, i) => (
                      <div key={`e${i}`} className="admin-img-thumb-wrap">
                        <img src={imgUrl(url, { w: 180 })} className="admin-img-thumb" alt="" loading="lazy" />
                        {i === 0 && <span className="admin-img-main-badge">Main</span>}
                        <button className="admin-img-remove-x" type="button"
                          onClick={() => removeVariantExisting(v.key, i)} aria-label="Remove image">×</button>
                      </div>
                    ))}
                    {(v.files || []).map((item, i) => (
                      <div key={`n${i}`} className="admin-img-thumb-wrap">
                        <img src={item.preview} className="admin-img-thumb" alt="" />
                        {(v.existing?.length || 0) === 0 && i === 0
                          ? <span className="admin-img-main-badge">Main</span>
                          : <span className="admin-img-new-badge">New</span>}
                        <button className="admin-img-remove-x" type="button"
                          onClick={() => removeVariantNewPhoto(v.key, i)} aria-label="Remove image">×</button>
                      </div>
                    ))}
                  </div>
                )}

                <label className="admin-img-upload-area admin-img-upload-area--compact">
                  <span className="admin-img-upload-text">
                    {total > 0 ? "Add more photos" : "Add photos for this colour"}
                  </span>
                  <input type="file" accept="image/*" multiple
                    onChange={(e) => addVariantPhotos(v.key, e)} hidden />
                </label>
              </div>
            );
          })}

          <button type="button" className="admin-variant-add" onClick={addVariant}>
            {Icons.plus ? Icons.plus(16) : "+"} Add a colour
          </button>
        </fieldset>
      </>
    );
  };

  /* ================= LOGIN SCREEN ================= */
  if (!isAuth) {
    return (
      <div className="admin-login">
        <div className="admin-login-card">
          <span className="admin-login-icon">{Icons.lock(28)}</span>
          <h2>Admin sign in</h2>
          <p className="admin-login-sub">Enter your password to manage products and categories.</p>
          <input type="password" className="form-input" placeholder="Admin password"
            value={inputPass} onChange={(e) => setInputPass(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleLogin()} autoFocus />
          {loginError && <div className="msg-error">{loginError}</div>}
          <button className="btn-primary admin-login-btn" onClick={handleLogin}>Sign in</button>
        </div>
      </div>
    );
  }

  /* ================= PRODUCT LIST ================= */
  const filteredProducts = listFilter
    ? products.filter((p) => {
        const q = listFilter.toLowerCase();
        return (p.name || "").toLowerCase().includes(q)
          || (p.cat || "").toLowerCase().includes(q)
          || productSubcat(p).toLowerCase().includes(q);
      })
    : products;

  /* ================= MAIN UI ================= */
  return (
    <div className="admin-page">
      <div className="page-header" data-echo="ADMIN">
        <h1>ADMIN PANEL</h1>
        <p>Products · Categories · Settings</p>
      </div>

      <div className="admin-shell">
        <div className="admin-tabs" role="tablist">
          {[
            ["add", "Add product"],
            ["list", `Products (${products.length})`],
            ["cats", "Categories"],
            ...(isEditing ? [["edit", "Editing"]] : []),
            ["settings", "Settings"],
          ].map(([id, label]) => (
            <button key={id} role="tab" aria-selected={tab === id}
              className={tab === id ? "active" : ""} onClick={() => setTab(id)}>
              {label}
            </button>
          ))}
        </div>

        {/* ── ADD PRODUCT ─────────────────────────────── */}
        {tab === "add" && (
          <div className="admin-card">
            {renderFormFields(false)}
            <div className="admin-actions">
              <button className="btn-primary" onClick={handleAdd} disabled={uploading}>
                {uploading ? "Uploading…" : "Add product"}
              </button>
              <button className="admin-ghost-btn" onClick={resetForm} disabled={uploading}>
                Clear form
              </button>
            </div>
          </div>
        )}

        {/* ── EDIT PRODUCT ────────────────────────────── */}
        {tab === "edit" && isEditing && (
          <div className="admin-card">
            <div className="admin-edit-header">
              <span>Editing “{form.name}”</span>
              <button className="admin-ghost-btn" onClick={cancelEdit}>Cancel</button>
            </div>
            {renderFormFields(true)}
            <div className="admin-actions">
              <button className="btn-primary" onClick={handleUpdate} disabled={editUploading}>
                {editUploading ? "Saving…" : "Save changes"}
              </button>
              <button className="admin-ghost-btn" onClick={cancelEdit} disabled={editUploading}>
                Discard
              </button>
            </div>
          </div>
        )}

        {/* ── PRODUCT LIST ────────────────────────────── */}
        {tab === "list" && (
          <div className="admin-card">
            <div className="admin-list-head">
              <input className="form-input admin-search" placeholder="Search products…"
                value={listFilter} onChange={(e) => setListFilter(e.target.value)} />
              <span className="admin-list-count">
                {filteredProducts.length} of {products.length}
              </span>
            </div>

            {products.length === 0 ? (
              <div className="admin-empty">
                No products yet. Use the “Add product” tab to create the first one.
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="admin-empty">Nothing matches “{listFilter}”.</div>
            ) : (
              <div className="admin-table">
                {filteredProducts.map((p) => {
                  const sub = productSubcat(p);
                  const det = Array.isArray(p.detectedColors) ? p.detectedColors : [];
                  const setColorName = (p.colorName || (p.color && p.color.name) || "").trim();
                  const setColorHex = (p.color && p.color.hex) || (det[0] && det[0].hex) || "#CCCCCC";
                  return (
                    <div key={p.id} className="admin-product-row">
                      {p.img
                        ? <img src={imgUrl(p.img, { w: 120 })} className="admin-product-thumb" alt="" loading="lazy" />
                        : <div className="admin-product-thumb-placeholder">{Icons.diamond(20)}</div>}

                      <div className="admin-product-info">
                        <div className="admin-product-name">{p.name}</div>
                        <div className="admin-product-meta">
                          <span className="admin-tag">{p.cat || "Uncategorised"}</span>
                          {sub && <span className="admin-tag admin-tag--sub">{sub}</span>}
                          {typeof p.stock === "number" && p.stock <= 0 && (
                            <span className="admin-tag admin-tag--warn">Sold out</span>
                          )}
                          {p.images?.length > 1 && (
                            <span className="admin-product-meta__dim">{p.images.length} photos</span>
                          )}
                          {p.dimensions && <span className="admin-product-meta__dim">{p.dimensions}</span>}
                        </div>
                        {(setColorName || det.length > 0) && (
                          <div className="admin-product-colors">
                            <span className="admin-product-color-dot"
                              style={{ background: setColorHex }}
                              title={setColorName ? `${setColorName} — ${setColorHex}` : setColorHex} />
                            <span className="admin-product-color-name">
                              {setColorName || det[0].name}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="admin-product-price">{Rs(p.price)}</div>

                      <div className="admin-product-actions">
                        <button className="admin-edit-btn" onClick={() => startEdit(p)}>Edit</button>
                        <button className="admin-delete-btn" onClick={() => handleDelete(p.id)}>Delete</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── CATEGORY MANAGER ────────────────────────── */}
        {tab === "cats" && (
          <div className="admin-card">
            {catMsg && (
              <div className={catMsg.ok ? "msg-success" : "msg-error"}>
                {catMsg.ok && Icons.check()} {catMsg.text}
              </div>
            )}

            <fieldset className="admin-fieldset">
              <legend>Add a subcategory</legend>
              <p className="admin-hint">
                Pick one of your existing categories, type the new subcategory
                name, and add it. It shows up in the shop right away.
              </p>
              <div className="admin-inline-form">
                <select
                  className="form-select"
                  value={quickSubCat}
                  onChange={(e) => setQuickSubCat(e.target.value)}
                  aria-label="Category"
                >
                  <option value="">— Select a category —</option>
                  {catNames.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <input className="form-input"
                  placeholder={quickSubCat ? `New subcategory in ${quickSubCat} — e.g. Zircon` : "Subcategory name — e.g. Zircon"}
                  value={quickSubName} onChange={(e) => setQuickSubName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleQuickAddSub()} />
                <button className="btn-primary" onClick={handleQuickAddSub} disabled={catBusy}>
                  Add subcategory
                </button>
              </div>
            </fieldset>

            <fieldset className="admin-fieldset">
              <legend>Create a category</legend>
              <div className="admin-inline-form">
                <input className="form-input" placeholder="Category name — e.g. Rings"
                  value={newCatName} onChange={(e) => setNewCatName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddCategory()} />
                <button className="btn-primary" onClick={handleAddCategory} disabled={catBusy}>
                  Add category
                </button>
              </div>
            </fieldset>

            {necklaceCats.length > 0 && categories.some((c) => c.name === "Necklaces") && (
              <div className="admin-migrate">
                <div>
                  <strong>{necklaceCats.length} older necklace categories found.</strong>
                  <p>They already work as subcategories. Import them to manage them here too.</p>
                </div>
                <button className="admin-mini-btn" onClick={handleImportLegacy} disabled={catBusy}>
                  Import
                </button>
              </div>
            )}

            <fieldset className="admin-fieldset">
              <legend>Category hierarchy</legend>

              {categories.length === 0 ? (
                <div className="admin-empty">
                  {catNames.length > 0 ? (
                    <>Your categories ({catNames.join(", ")}) will appear here as a
                      tree once you add a subcategory to them using the panel above —
                      no need to re-create anything.</>
                  ) : (
                    <>No categories yet. Create one above, or add your first product —
                      its category will show up here automatically.</>
                  )}
                </div>
              ) : (
                <ul className="admin-tree">
                  {categories.map((cat) => {
                    const subs = subcatsFor(cat.name).filter((s) => !s.derived);
                    const derivedSubs = subcatsFor(cat.name).filter((s) => s.derived);
                    const open = openCatId === cat.id;
                    return (
                      <li key={cat.id} className={`admin-tree__cat${open ? " is-open" : ""}`}>
                        <div className="admin-tree__row">
                          <button className="admin-tree__toggle"
                            onClick={() => { setOpenCatId(open ? null : cat.id); setNewSubName(""); }}
                            aria-expanded={open}>
                            <span className={`admin-tree__caret${open ? " is-open" : ""}`} aria-hidden="true">▸</span>
                          </button>

                          {editingCatId === cat.id ? (
                            <div className="admin-inline-form admin-inline-form--tight">
                              <input className="form-input" value={editingCatName} autoFocus
                                onChange={(e) => setEditingCatName(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleRenameCategory(cat.id)} />
                              <button className="admin-mini-btn admin-mini-btn--primary"
                                onClick={() => handleRenameCategory(cat.id)} disabled={catBusy}>Save</button>
                              <button className="admin-mini-btn" onClick={() => setEditingCatId(null)}>Cancel</button>
                            </div>
                          ) : (
                            <>
                              <span className="admin-tree__name">{cat.name}</span>
                              <span className="admin-tree__meta">
                                {subs.length + derivedSubs.length} sub · {catCount(cat.name)} products
                              </span>
                              <div className="admin-tree__actions">
                                <button className="admin-mini-btn"
                                  onClick={() => { setEditingCatId(cat.id); setEditingCatName(cat.name); }}>
                                  Rename
                                </button>
                                <button className="admin-mini-btn admin-mini-btn--danger"
                                  onClick={() => handleDeleteCategory(cat.id, cat.name)} disabled={catBusy}>
                                  Delete
                                </button>
                              </div>
                            </>
                          )}
                        </div>

                        {open && (
                          <div className="admin-tree__body">
                            <ul className="admin-tree__subs">
                              {subs.length === 0 && derivedSubs.length === 0 && (
                                <li className="admin-tree__empty">No subcategories yet.</li>
                              )}

                              {subs.map((s) => (
                                <li key={s.id} className="admin-tree__sub">
                                  {editingSubId === s.id ? (
                                    <div className="admin-inline-form admin-inline-form--tight">
                                      <input className="form-input" value={editingSubName} autoFocus
                                        onChange={(e) => setEditingSubName(e.target.value)}
                                        onKeyDown={(e) => e.key === "Enter" && handleRenameSub(s.id)} />
                                      <button className="admin-mini-btn admin-mini-btn--primary"
                                        onClick={() => handleRenameSub(s.id)} disabled={catBusy}>Save</button>
                                      <button className="admin-mini-btn" onClick={() => setEditingSubId(null)}>Cancel</button>
                                    </div>
                                  ) : (
                                    <>
                                      <span className="admin-tree__name">{s.name}</span>
                                      <span className="admin-tree__meta">
                                        {subcatCount(cat.name, s.name)} products
                                      </span>
                                      <div className="admin-tree__actions">
                                        <button className="admin-mini-btn"
                                          onClick={() => { setEditingSubId(s.id); setEditingSubName(s.name); }}>
                                          Rename
                                        </button>
                                        <button className="admin-mini-btn admin-mini-btn--danger"
                                          onClick={() => handleDeleteSub(s.id, s.name)} disabled={catBusy}>
                                          Delete
                                        </button>
                                      </div>
                                    </>
                                  )}
                                </li>
                              ))}

                              {derivedSubs.map((s) => (
                                <li key={s.id} className="admin-tree__sub admin-tree__sub--derived">
                                  <span className="admin-tree__name">{s.name}</span>
                                  <span className="admin-tree__meta">
                                    {subcatCount(cat.name, s.name)} products · not saved as a subcategory yet
                                  </span>
                                  <div className="admin-tree__actions">
                                    <button className="admin-mini-btn"
                                      onClick={async () => {
                                        setCatBusy(true);
                                        const res = await addSubcategory(cat.id, s.name);
                                        setCatBusy(false);
                                        flash(res.ok, res.ok ? "Subcategory saved." : res.msg);
                                      }} disabled={catBusy}>
                                      Save it
                                    </button>
                                  </div>
                                </li>
                              ))}
                            </ul>

                            <div className="admin-inline-form admin-inline-form--tight">
                              <input className="form-input" placeholder={`New subcategory in ${cat.name}`}
                                value={newSubName} onChange={(e) => setNewSubName(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleAddSub(cat.id)} />
                              <button className="admin-mini-btn admin-mini-btn--primary"
                                onClick={() => handleAddSub(cat.id)} disabled={catBusy}>
                                Add subcategory
                              </button>
                            </div>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </fieldset>
          </div>
        )}

        {/* ── SETTINGS ────────────────────────────────── */}
        {tab === "settings" && (
          <div className="admin-card">
            {settingsOk && <div className="msg-success">{Icons.check()} Settings saved.</div>}

            <fieldset className="admin-fieldset">
              <legend>Store settings</legend>

              <div className="admin-field">
                <label htmlFor="s-wa">WhatsApp number</label>
                <input id="s-wa" className="form-input" value={swa}
                  onChange={(e) => setSwa(e.target.value)} placeholder="923001234567" />
                <p className="admin-hint">Include the country code, with no plus sign or spaces.</p>
              </div>

              <div className="admin-field">
                <label htmlFor="s-pass">New admin password</label>
                <input id="s-pass" type="password" className="form-input" value={spass}
                  onChange={(e) => setSpass(e.target.value)} placeholder="Leave blank to keep the current one" />
              </div>

              <div className="admin-actions">
                <button className="btn-primary" onClick={handleSaveSettings}>Save settings</button>
              </div>
            </fieldset>
          </div>
        )}
      </div>
    </div>
  );
}
